import routes from '../routing/routes.description'

/**
 * Tenant validation and sanitization utilities.
 *
 * Centralises tenant slug validation to prevent injection attacks via
 * URL path segments, OIDC acr_values query parameters, and HTTP headers.
 * All tenant identifiers MUST pass through validateTenant() before being
 * used in URLs, headers, or localStorage.
 */

/**
 * Strict regex for valid Retain tenant slugs.
 * Matches the server-side pattern in Essentials TenantValidationMiddleware:
 *   ^[a-zA-Z0-9][a-zA-Z0-9_-]{0,127}$
 * Rejects empty strings, strings starting with hyphens/underscores,
 * and any string containing characters that could pollute query strings
 * (spaces, &, =, ?, #, etc.).
 */
const TENANT_SLUG_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,127}$/

/**
 * System-reserved path segments that exist outside of React Router.
 * These are infrastructure endpoints, static assets, and callback handlers.
 */
export const SYSTEM_RESERVED_SEGMENTS = new Set([
  'openid-complete',
  'silent_renew',
  'login',
  'logout',
  'callback',
  'static',
  'essentials',
  'api',
  '401',
  '403',
  '404',
])

/**
 * Dynamically extract all top-level path segments from registered application routes.
 * Automatically inspects routes.description.js so any newly added routes or
 * modules are automatically recognized without manually editing a static list.
 *
 * @returns {Set<string>}
 */
export const getReservedPathSegments = () => {
  const segments = new Set(SYSTEM_RESERVED_SEGMENTS)
  // Fail loudly rather than silently degrading. `routes` is undefined only when
  // a circular import is reintroduced (routes.description -> component ->
  // container -> utils/api -> tenantUtils -> routes.description). Every
  // component in routes.description.js must stay behind React.lazy() to keep
  // that cycle broken. Degrading quietly here would shrink the reserved list to
  // the system segments alone, and /talent-profile would be read as a tenant.
  if (!Array.isArray(routes)) {
    throw new Error(
      'tenantUtils: routes.description.js did not export an array. This means a ' +
        'circular import was reintroduced — ensure every route component uses ' +
        'React.lazy(). Tenant resolution cannot be trusted in this state.',
    )
  }
  routes.forEach((route) => {
    const paths = Array.isArray(route?.path) ? route.path : [route?.path]
    paths.forEach((p) => {
      if (typeof p === 'string' && p.startsWith('/') && p !== '/') {
        const firstSeg = p.split('/')[1]
        if (firstSeg && firstSeg !== '*') {
          segments.add(firstSeg.toLowerCase())
        }
      }
    })
  })
  return segments
}

/**
 * All reserved path segments (system + dynamically extracted application routes).
 * Any newly added application module in routes.description.js is automatically included.
 */
export const RESERVED_PATH_SEGMENTS = getReservedPathSegments()

/** localStorage key for the cached tenant slug (not the full acr_values). */
export const TENANT_STORAGE_KEY = 'rpc-tenant'

/** localStorage key for the cached acr_values string. */
export const TENANT_ACR_STORAGE_KEY = 'rpc-tenantAcr'

/**
 * Validate a tenant slug against the strict pattern.
 * Returns the slug if valid, or null if invalid/empty.
 *
 * @param {string|null|undefined} slug - The raw tenant slug to validate.
 * @returns {string|null} The validated slug, or null.
 */
export const validateTenant = (slug) => {
  if (!slug || typeof slug !== 'string') return null
  const trimmed = slug.trim()
  if (!trimmed || RESERVED_PATH_SEGMENTS.has(trimmed.toLowerCase())) return null
  return TENANT_SLUG_PATTERN.test(trimmed) ? trimmed : null
}

/**
 * Extract tenant slug from a URL pathname.
 * Takes the first path segment and validates it.
 *
 * @param {string} [pathname] - Defaults to window.location.pathname.
 * @returns {string|null} Validated tenant slug or null.
 */
export const getTenantFromPath = (pathname = window.location.pathname) => {
  const firstSegment = pathname.split('/')[1] || ''
  return validateTenant(firstSegment)
}

/**
 * Build a sanitised acr_values string for a tenant slug.
 * Returns null if the slug is invalid.
 *
 * @param {string|null|undefined} slug - The tenant slug.
 * @returns {string|null} e.g. 'tenant:mycompany' or null.
 */
export const buildAcrValues = (slug) => {
  const validated = validateTenant(slug)
  return validated ? `tenant:${validated}` : null
}

/**
 * Read the cached tenant slug from localStorage.
 * Returns null if absent, invalid, or storage is unavailable.
 *
 * @returns {string|null}
 */
export const getCachedTenant = () => {
  try {
    return validateTenant(window.localStorage.getItem(TENANT_STORAGE_KEY))
  } catch {
    return null
  }
}

/**
 * Save a validated tenant slug to localStorage.
 * Silently no-ops if the slug is invalid or storage is unavailable.
 *
 * @param {string|null|undefined} slug
 */
export const setCachedTenant = (slug) => {
  const validated = validateTenant(slug)
  try {
    if (validated) {
      window.localStorage.setItem(TENANT_STORAGE_KEY, validated)
    } else {
      window.localStorage.removeItem(TENANT_STORAGE_KEY)
    }
  } catch {
    // Storage unavailable
  }
}

/**
 * Read the default tenant slug from environment variable REACT_APP_IDENTITY_ACR_VALUES.
 * Format expected: "tenant:<slug>" (e.g. "tenant:8lien").
 * Returns null if absent, invalid, or malformed.
 *
 * @returns {string|null}
 */
export const getEnvTenant = () => {
  try {
    const envAcr = process.env.REACT_APP_IDENTITY_ACR_VALUES
    if (!envAcr || typeof envAcr !== 'string') return null
    const match = envAcr.trim().match(/^tenant:(.+)$/)
    return match ? validateTenant(match[1]) : null
  } catch {
    return null
  }
}

/**
 * Extract tenant slug from URL search query parameters (?acr_values=tenant:xyz or ?tenant=xyz).
 *
 * @param {string} [search] - Defaults to window.location.search.
 * @returns {string|null} Validated tenant slug or null.
 */
export const getTenantFromQuery = (
  search = (typeof window !== 'undefined' && window.location.search) || '',
) => {
  try {
    if (!search || typeof search !== 'string') return null
    const params = new URLSearchParams(search)
    const acr = params.get('acr_values')
    if (acr) {
      const match = acr.trim().match(/^tenant:(.+)$/)
      if (match) {
        const validated = validateTenant(match[1])
        if (validated) return validated
      }
    }
    const tenantParam = params.get('tenant')
    if (tenantParam) {
      const validated = validateTenant(tenantParam)
      if (validated) return validated
    }
  } catch {
    return null
  }
  return null
}

/**
 * Resolve the active tenant using the priority chain:
 *   1. URL path segment (/{tenant}/...)
 *   2. Cached tenant in localStorage (rpc-tenant) — written from the signed JWT
 *   3. Configured environment variable (REACT_APP_IDENTITY_ACR_VALUES)
 *
 * SECURITY: the URL *query string* is deliberately NOT a source here.
 * `resolveTenant()` feeds `ensureTenantInUrl()`, the router basename and
 * `TenantGuard`. Honouring `?tenant=` let any third-party link rewrite the path
 * to an arbitrary tenant, which `TenantGuard` then read as a tenant switch and
 * turned into a forced sign-out — a logout CSRF. Use `getTenantFromQuery()`
 * explicitly, and only as a pre-authentication `acr_values` hint, never as an
 * input to a routing or sign-out decision.
 *
 * All values are validated via validateTenant().
 *
 * @returns {string|null} Validated tenant slug or null.
 */
export const resolveTenant = () =>
  getTenantFromPath() || getCachedTenant() || getEnvTenant()

/**
 * Ensure the current browser URL includes the tenant slug prefix (/{tenant}/...).
 *
 * When an authenticated or default tenant is known but the user accessed a top-level
 * path directly (e.g. /talent-profile or /data-import or /), this redirects the
 * browser via window.location.replace to /{tenant}{pathname}.
 *
 * Excludes:
 *  - Authentication callbacks (?code=, /openid-complete, /callback)
 *  - Silent renew iframes (/silent_renew)
 *  - URLs that already contain the valid tenant prefix (/{tenant}/... or /{tenant})
 *
 * @param {string} [pathname] - Defaults to window.location.pathname.
 * @returns {boolean} True if a redirect was initiated, false otherwise.
 */
export const ensureTenantInUrl = (
  pathname = (typeof window !== 'undefined' && window.location.pathname) || '',
) => {
  if (typeof window === 'undefined' || !window.location) return false

  const tenant = resolveTenant()
  if (!tenant) return false

  const search = window.location.search || ''
  const isCallback =
    search.includes('code=') ||
    search.includes('error=') ||
    pathname === '/openid-complete' ||
    pathname.endsWith('/openid-complete') ||
    pathname === '/callback' ||
    pathname.endsWith('/callback')
  const isSilentRenew =
    pathname === '/silent_renew' || pathname.endsWith('/silent_renew')

  if (isCallback || isSilentRenew) return false

  const tenantPrefix = `/${tenant}`
  if (pathname === tenantPrefix || pathname.startsWith(`${tenantPrefix}/`)) {
    return false
  }

  const targetPath =
    pathname === '/' ? `${tenantPrefix}/` : `${tenantPrefix}${pathname}`
  const targetUrl = `${targetPath}${search}${window.location.hash || ''}`
  window.location.replace(targetUrl)
  return true
}
